/**
 * 1. pálya — BEHATOLÁS
 *
 * Térdramaturgia (C): szűk indulás → hosszú, sötét folyosó (klausztrofóbia)
 * → nagy csarnok oszlopokkal (első valódi harc, két irányból) → lifttel
 * elérhető erkély, ahonnan vissza lehet lőni a csarnokba (jutalom a
 * felderítésért) → titkos raktár → karbantartó-folyosó (pihenő, sörétes)
 * → kaputerem (kulcs) → visszaút → zárt zsilip → kijárat.
 *
 * A geometria C gazdája; az ENCOUNTER-elhelyezés (`things` ellenség-sorai)
 * X-é — az arányokat és a csoportosítást ő hangolja a GDD 4. pontja szerint.
 */

import type { LevelDef } from '../../world/level'
import { box, v } from '../../world/level'

export const LEVEL_1: LevelDef = {
  id: 'l1',
  name: 'Behatolás',
  subtitle: 'Külső zsilip · 4. szint',
  skyColor: 0x0a0d12,
  fogColor: 0x161d26,
  fogDensity: 0.0155,
  ambient: 0.42,

  rooms: [
    {
      id: 1,
      name: 'Leszállóöböl',
      poly: box(-7, -6, 7, 4),
      floorY: 0,
      ceilY: 5,
      light: 0.62,
      tex: { floor: 'floorTile', ceil: 'ceilingTech', wall: 'techPanel' },
    },
    {
      id: 2,
      name: 'Beléptető folyosó',
      poly: box(-2, 4, 2, 18),
      floorY: 0,
      ceilY: 3.4,
      light: 0.38,
      tex: { floor: 'grate', ceil: 'metalRibbed', wall: 'metalRibbed' },
    },
    {
      id: 3,
      name: 'Fő csarnok',
      poly: box(-10, 18, 14, 44),
      floorY: 0,
      ceilY: 9,
      light: 0.46,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'brick' },
    },
    {
      id: 4,
      name: 'Teherlift',
      poly: box(14, 20, 18, 26),
      floorY: 0,
      ceilY: 9,
      light: 0.44,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalPlate' },
      lift: { lowY: 0, highY: 4, speed: 2.4, waitTime: 3.5, startAt: 'low', autoTrigger: true },
    },
    {
      id: 5,
      name: 'Kezelőerkély',
      poly: box(14, 26, 26, 40),
      floorY: 4,
      ceilY: 9,
      light: 0.4,
      tex: { floor: 'grate', ceil: 'ceilingTech', wall: 'metalPlate' },
    },
    {
      id: 6,
      name: 'Zárt raktár',
      poly: box(26, 28, 34, 36),
      floorY: 4,
      ceilY: 7,
      light: 0.26,
      secret: true,
      tex: { floor: 'floorTile', ceil: 'ceilingTech', wall: 'techPanel' },
    },
    {
      id: 7,
      name: 'Karbantartó',
      poly: box(-10, 44, 2, 52),
      floorY: 0,
      ceilY: 4,
      light: 0.34,
      tex: { floor: 'floorTile', ceil: 'metalRibbed', wall: 'concrete' },
    },
    {
      id: 8,
      name: 'Kaputerem',
      poly: box(2, 44, 16, 60),
      floorY: 0,
      ceilY: 6.5,
      light: 0.42,
      tex: { floor: 'bloodStone', ceil: 'ceilingTech', wall: 'brick' },
    },
    {
      id: 9,
      name: 'Kijárati zsilip',
      poly: box(-10, 52, 2, 62),
      floorY: 0,
      ceilY: 5,
      light: 0.66,
      tex: { floor: 'floorTile', ceil: 'lightPanel', wall: 'techPanel' },
    },
  ],

  portals: [
    { id: 1, rooms: [1, 2], p0: v(-2, 4), p1: v(2, 4) },
    { id: 2, rooms: [2, 3], p0: v(-2, 18), p1: v(2, 18) },
    // A csarnok és a lift között teljes magasságú nyílás.
    { id: 3, rooms: [3, 4], p0: v(14, 20), p1: v(14, 26) },
    // A lift felső állásában nyílik meg az erkély felé.
    { id: 4, rooms: [4, 5], p0: v(14, 26), p1: v(18, 26) },
    // Az erkély pereme: felülről le lehet ugrani a csarnokba, vissza nem.
    { id: 5, rooms: [3, 5], p0: v(14, 26), p1: v(14, 40) },
    { id: 6, rooms: [5, 6], p0: v(26, 28), p1: v(26, 36) },
    { id: 7, rooms: [3, 7], p0: v(-10, 44), p1: v(2, 44) },
    { id: 8, rooms: [3, 8], p0: v(2, 44), p1: v(14, 44) },
    {
      id: 9,
      rooms: [7, 9],
      p0: v(-10, 52),
      p1: v(-2, 52),
      door: { kind: 'door', speed: 2.6, waitTime: 0, key: 'red', tex: 'doorTech' },
    },
  ],

  props: [
    // --- Csarnok-oszlopok: fedezék, és megtörik a nagy üres teret ---
    { id: 1, kind: 'box', pos: v(-5, 24), size: v(1.1, 1.1), bottom: 0, top: 9, tex: 'brick', solid: true },
    { id: 2, kind: 'box', pos: v(9, 24), size: v(1.1, 1.1), bottom: 0, top: 9, tex: 'brick', solid: true },
    { id: 3, kind: 'box', pos: v(-5, 38), size: v(1.1, 1.1), bottom: 0, top: 9, tex: 'brick', solid: true },
    { id: 4, kind: 'box', pos: v(9, 38), size: v(1.1, 1.1), bottom: 0, top: 9, tex: 'brick', solid: true },

    // --- Rálépehető ládák (top <= 0.45 fölött is: ezekre ugrani kell) ---
    { id: 5, kind: 'box', pos: v(2, 30), size: v(1.4, 1.4), bottom: 0, top: 1.2, tex: 'metalPlate', solid: true },
    { id: 6, kind: 'box', pos: v(4.4, 31.6), size: v(1.1, 1.1), bottom: 0, top: 2.1, tex: 'metalPlate', solid: true },
    { id: 7, kind: 'box', pos: v(-7, 32), size: v(1.2, 1.6), bottom: 0, top: 1.5, tex: 'metalPlate', solid: true },

    // --- Világító panelek a csarnok falán ---
    { id: 8, kind: 'box', pos: v(-9.7, 28), size: v(0.15, 2.4), bottom: 5.2, top: 6.4, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 9, kind: 'box', pos: v(-9.7, 36), size: v(0.15, 2.4), bottom: 5.2, top: 6.4, tex: 'lightPanel', solid: false, emissive: 1 },
    { id: 10, kind: 'box', pos: v(13.7, 21), size: v(0.15, 1.6), bottom: 5.2, top: 6.4, tex: 'lightPanel', solid: false, emissive: 1 },

    // --- Leszállóöböl berendezése ---
    { id: 11, kind: 'cylinder', pos: v(-5, -3.5), size: v(0.7, 0.7), bottom: 0, top: 2.4, tex: 'metalRibbed', solid: true },
    { id: 12, kind: 'cylinder', pos: v(5, -3.5), size: v(0.7, 0.7), bottom: 0, top: 2.4, tex: 'metalRibbed', solid: true },
    { id: 13, kind: 'box', pos: v(0, 3.2), size: v(2.6, 0.3), bottom: 0, top: 0.35, tex: 'hazardStripe', solid: false },

    // --- Kaputerem: emelvény a kulcs alatt ---
    { id: 14, kind: 'box', pos: v(9, 56), size: v(2.2, 2.2), bottom: 0, top: 0.9, tex: 'metalPlate', solid: true },
    { id: 15, kind: 'box', pos: v(9, 56), size: v(1.4, 1.4), bottom: 0.9, top: 1.35, tex: 'hazardStripe', solid: true },
    { id: 16, kind: 'cylinder', pos: v(4, 47), size: v(0.6, 0.6), bottom: 0, top: 6.5, tex: 'metalRibbed', solid: true },
    { id: 17, kind: 'cylinder', pos: v(14, 47), size: v(0.6, 0.6), bottom: 0, top: 6.5, tex: 'metalRibbed', solid: true },

    // --- Erkély korlátja (átlátni felette, átesni nem) ---
    { id: 18, kind: 'box', pos: v(14.15, 33), size: v(0.15, 7), bottom: 4, top: 5.1, tex: 'metalRibbed', solid: true },

    // --- Kijárati zsilip ---
    { id: 19, kind: 'box', pos: v(-4, 61.6), size: v(2.4, 0.3), bottom: 0, top: 2.8, tex: 'hazardStripe', solid: false },
  ],

  lights: [
    { pos: v(0, -1), y: 4.2, color: 0xffd98a, intensity: 12, distance: 16, style: 'steady' },
    { pos: v(0, 8), y: 3, color: 0x64c4cf, intensity: 7, distance: 12, style: 'flicker' },
    { pos: v(0, 15), y: 3, color: 0xffd98a, intensity: 6, distance: 11, style: 'steady' },
    { pos: v(-5, 22), y: 7, color: 0xe4b02c, intensity: 14, distance: 20, style: 'steady' },
    { pos: v(8, 34), y: 7, color: 0xe4b02c, intensity: 12, distance: 20, style: 'flicker' },
    { pos: v(-9, 32), y: 5.8, color: 0xffd98a, intensity: 9, distance: 13, style: 'pulse' },
    { pos: v(16, 23), y: 6, color: 0x64c4cf, intensity: 8, distance: 12, style: 'steady' },
    { pos: v(20, 33), y: 7, color: 0x64c4cf, intensity: 10, distance: 16, style: 'steady' },
    { pos: v(30, 32), y: 6, color: 0x8affff, intensity: 7, distance: 11, style: 'pulse' },
    { pos: v(-4, 48), y: 3.4, color: 0xffd98a, intensity: 8, distance: 12, style: 'flicker' },
    { pos: v(9, 50), y: 5.4, color: 0xe4b02c, intensity: 12, distance: 18, style: 'steady' },
    { pos: v(9, 57), y: 5.4, color: 0xff3b1f, intensity: 9, distance: 14, style: 'pulse' },
    { pos: v(-4, 57), y: 4.4, color: 0xffffff, intensity: 14, distance: 15, style: 'steady' },
  ],

  things: [
    { kind: 'playerStart', type: 'player', pos: v(0, -2), yaw: 0 },

    // === ENCOUNTEREK — gazda: X (GDD 4. pont: 10 grunt, 5 imp, 3 hound) ===

    // Oktatófolyosó: 3 grunt. FONTOS, hogy a startból NE legyenek látótávban
    // és NE a játékos felé nézzenek: az első lövés a játékosé kell legyen,
    // különben a becsapódás előtt nincs esély reagálni. Ezért mind a három
    // a folyosó túlvégén, HÁTTAL áll (yaw: 0 = +Z, vagyis elfelé), és a
    // legközelebbi is a folyosó felénél túl van.
    { kind: 'enemy', type: 'grunt', pos: v(-0.9, 15.5), yaw: 0, group: 'a' },
    { kind: 'enemy', type: 'grunt', pos: v(1.1, 17.2), yaw: 0.4, group: 'a' },
    // A harmadik már a csarnokban, oldalt — akkor veszi észre a játékost,
    // amikor az kilép a folyosóból.
    { kind: 'enemy', type: 'grunt', pos: v(-7, 21), yaw: 1.9, group: 'a' },

    // Első aréna: 4 grunt + 1 imp, két irányból.
    { kind: 'enemy', type: 'grunt', pos: v(-7, 24), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'grunt', pos: v(7, 25), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'grunt', pos: v(-3, 34), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'grunt', pos: v(11, 33), yaw: Math.PI, group: 'b' },
    { kind: 'enemy', type: 'imp', pos: v(2, 40), yaw: Math.PI, group: 'b' },

    // Oldalsó rajtaütés az erkélyről.
    { kind: 'enemy', type: 'hound', pos: v(20, 30), yaw: Math.PI, group: 'c' },
    { kind: 'enemy', type: 'hound', pos: v(23, 36), yaw: Math.PI, group: 'c' },

    // Kapuszoba: 3 imp + 3 grunt — lövedék és hitscan együtt.
    { kind: 'enemy', type: 'imp', pos: v(6, 50), yaw: Math.PI, group: 'd' },
    { kind: 'enemy', type: 'imp', pos: v(13, 51), yaw: Math.PI, group: 'd' },
    { kind: 'enemy', type: 'imp', pos: v(13, 58), yaw: Math.PI, group: 'd' },
    { kind: 'enemy', type: 'grunt', pos: v(4, 57), yaw: Math.PI, group: 'd' },
    { kind: 'enemy', type: 'grunt', pos: v(14, 56), yaw: Math.PI, group: 'd' },
    { kind: 'enemy', type: 'grunt', pos: v(11, 46), yaw: Math.PI, group: 'd' },

    // Zárófolyosó: 1 imp + 1 hound a visszaúton.
    { kind: 'enemy', type: 'imp', pos: v(-7, 56), yaw: 0, group: 'e' },
    { kind: 'enemy', type: 'hound', pos: v(-7, 59), yaw: 0, group: 'e' },

    // === LŐSZER ÉS ÉLET (GDD 3. pont: 104 bullet, 16 shell, 2 kis medkit) ===
    { kind: 'pickup', type: 'bullets', pos: v(-4, 1) },
    { kind: 'pickup', type: 'bullets', pos: v(-8, 22) },
    { kind: 'pickup', type: 'bullets', pos: v(12, 28) },
    { kind: 'pickup', type: 'bullets', pos: v(-6, 41) },
    { kind: 'pickup', type: 'bullets', pos: v(12, 52) },
    // A sörétes a pihenőben — a fegyver és a nyugalom együtt jutalom.
    { kind: 'pickup', type: 'weapon:shotgun', pos: v(-6, 47) },
    { kind: 'pickup', type: 'shells', pos: v(-3, 47) },
    { kind: 'pickup', type: 'shells', pos: v(-8, 45.5) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(-4, 50) },
    { kind: 'pickup', type: 'medkitSmall', pos: v(-7, 45.5) },

    // Erkély: jutalom a felderítésért.
    { kind: 'pickup', type: 'armor', pos: v(22, 38) },

    // Titkos raktár.
    { kind: 'pickup', type: 'medkitLarge', pos: v(30, 32) },

    // Kulcs és kijárat.
    { kind: 'pickup', type: 'keyRed', pos: v(9, 56) },
    { kind: 'exit', type: 'exit', pos: v(-4, 60) },
  ],
}
